import React from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon } from '@heroicons/react/24/outline';

interface Metric {
  id: string;
  metric_name: string;
  metric_value: number;
  metric_unit?: string;
  metric_date: string;
  baseline_value?: number;
  target_value?: number;
  current_value?: number;
  category?: string;
}

interface MetricsOverviewProps {
  metrics: Metric[];
  focusId: string;
  onRefresh: () => void;
  compact?: boolean;
}

const MetricsOverview: React.FC<MetricsOverviewProps> = ({
  metrics,
  focusId,
  onRefresh,
  compact = false
}) => {
  const { darkMode } = useDarkMode();
  
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';

  if (!metrics || metrics.length === 0) {
    return (
      <div className={`${cardBg} ${borderColor} border rounded-lg shadow-sm p-6`}>
        <h3 className={`text-lg font-semibold ${textColor} mb-4`}>Key Metrics</h3>
        <div className="text-center py-8">
          <ArrowTrendingUpIcon className={`w-12 h-12 mx-auto ${mutedTextColor} mb-3`} />
          <p className={`${mutedTextColor} text-sm`}>
            Coming Soon
          </p>
          <p className={`${mutedTextColor} text-xs mt-2`}>
            Complete your setup to track metrics
          </p>
        </div>
      </div>
    );
  }

  // Get the latest value for each metric
  const latestMetrics = metrics.reduce((acc, metric) => {
    if (!acc[metric.metric_name] || new Date(metric.metric_date) > new Date(acc[metric.metric_name].metric_date)) {
      acc[metric.metric_name] = metric;
    }
    return acc;
  }, {} as Record<string, any>);

  const metricsList = Object.values(latestMetrics);

  // Compact view for sidebar
  if (compact) {
    // Special styling for key metrics in sidebar
    const priorityMetrics = metricsList.slice(0, 3).map((metric, index) => {
      const current = metric.current_value || metric.metric_value;
      const baseline = metric.baseline_value || 0;
      const improvement = baseline !== 0 ? ((current - baseline) / baseline) * 100 : 0;
      
      // Different background colors for each metric card
      const metricStyles = [
        {
          bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
          text: 'text-white'
        },
        {
          bg: darkMode ? 'bg-gray-700' : 'bg-green-50 border border-green-200',
          text: darkMode ? 'text-gray-100' : 'text-green-900'
        },
        {
          bg: darkMode ? 'bg-gray-700' : 'bg-purple-50 border border-purple-200',
          text: darkMode ? 'text-gray-100' : 'text-purple-900'
        }
      ];
      
      const style = metricStyles[index] || metricStyles[0];
      
      return (
        <div
          key={metric.id}
          className={`${style.bg} p-4 rounded-lg ${index === 0 ? style.text : ''}`}
        >
          <p className={`text-sm ${index === 0 ? 'opacity-90' : darkMode ? 'text-gray-300' : style.text}`}>
            {metric.metric_name}
          </p>
          <p className={`text-2xl font-bold mt-1 ${index === 0 ? '' : style.text}`}>
            {current}{metric.metric_unit ? ` ${metric.metric_unit}` : ''}
          </p>
          <p className={`text-xs mt-1 ${index === 0 ? 'opacity-75' : darkMode ? 'text-gray-400' : style.text.replace('900', '700')}`}>
            {improvement > 0 ? '↑' : improvement < 0 ? '↓' : '→'} {Math.abs(Math.round(improvement))}% from baseline
          </p>
        </div>
      );
    });
    
    return (
      <div className={`${cardBg} ${borderColor} border rounded-lg shadow-sm p-6`}>
        <h3 className={`text-lg font-semibold ${textColor} mb-4`}>Key Metrics</h3>
        <div className="space-y-3">
          {priorityMetrics}
        </div>
      </div>
    );
  }

  // Full view for main content area
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className={`text-lg font-semibold ${textColor}`}>Key Metrics</h2>
        <button
          onClick={onRefresh}
          className={`text-sm ${mutedTextColor} hover:text-purple-600 dark:hover:text-purple-400`}
        >
          Update Metrics
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricsList.slice(0, 4).map((metric) => {
          const current = metric.current_value || metric.metric_value;
          const baseline = metric.baseline_value || 0;
          const target = metric.target_value || baseline;
          const progress = target !== baseline ? ((current - baseline) / (target - baseline)) * 100 : 0;
          const trend = current > baseline ? 'up' : current < baseline ? 'down' : 'neutral';
          
          const TrendIcon = trend === 'up' ? ArrowTrendingUpIcon : 
                           trend === 'down' ? ArrowTrendingDownIcon : MinusIcon;
          const trendColor = trend === 'up' ? 'text-green-500' : 
                            trend === 'down' ? 'text-red-500' : 'text-gray-500';

          return (
            <div
              key={metric.id}
              className={`${cardBg} ${borderColor} border rounded-lg p-4`}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className={`text-sm font-medium ${textColor}`}>
                  {metric.metric_name}
                </h3>
                <TrendIcon className={`w-4 h-4 ${trendColor}`} />
              </div>

              <div className="mb-2">
                <p className={`text-2xl font-bold ${textColor}`}>
                  {current}
                  {metric.metric_unit && (
                    <span className={`text-sm font-normal ${mutedTextColor} ml-1`}>
                      {metric.metric_unit}
                    </span>
                  )}
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className={mutedTextColor}>Progress</span>
                  <span className={textColor}>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div
                    className="bg-purple-600 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs">
                  <span className={mutedTextColor}>
                    Base: {baseline}
                  </span>
                  <span className={mutedTextColor}>
                    Target: {target}
                  </span>
                </div>
              </div>

              {metric.category && (
                <div className="mt-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-100'
                  } ${mutedTextColor}`}>
                    {metric.category}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {metricsList.length > 4 && (
        <div className="mt-4 text-center">
          <button className={`text-sm ${mutedTextColor} hover:text-purple-600 dark:hover:text-purple-400`}>
            View All {metricsList.length} Metrics →
          </button>
        </div>
      )}
    </div>
  );
};

export default MetricsOverview;